import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Globe, Smartphone, Monitor, Tablet, MapPin, Link2,
  TrendingUp, Eye, Users, BarChart3, ArrowUpRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { supabase } from "@/integrations/supabase/client";

const formatNumber = (n: number) => n.toLocaleString("pt-BR");

const COLORS = [
  "hsl(24, 95%, 53%)", "hsl(200, 80%, 50%)", "hsl(142, 71%, 45%)",
  "hsl(38, 92%, 50%)", "hsl(280, 60%, 50%)", "hsl(0, 72%, 51%)",
  "hsl(180, 60%, 45%)", "hsl(320, 70%, 55%)"
];

const PERIOD_OPTIONS = [
  { label: "7 dias", days: 7 },
  { label: "14 dias", days: 14 },
  { label: "30 dias", days: 30 },
];

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
};

const AdminTrafficTab = () => {
  const [visits, setVisits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);

  const fetchVisits = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - period);

    const { data } = await supabase
      .from("page_visits")
      .select("*")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: false })
      .limit(5000) as any;

    setVisits(data || []);
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    fetchVisits();
    const interval = setInterval(() => fetchVisits(false), 60_000);
    return () => clearInterval(interval);
  }, [period]);

  // Real-time visitor count (last 5 minutes)
  const realtimeCount = useMemo(() => {
    const fiveMinAgo = new Date(Date.now() - 5 * 60_000).toISOString();
    return visits.filter(v => v.created_at >= fiveMinAgo).length;
  }, [visits]);

  // Unique sessions
  const uniqueSessions = useMemo(() => {
    const set = new Set(visits.map(v => v.session_id).filter(Boolean));
    return set.size;
  }, [visits]);

  // Daily visits chart
  const dailyData = useMemo(() => {
    const days: { date: string; label: string; views: number; sessions: number }[] = [];
    for (let i = period - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayVisits = visits.filter(v => v.created_at.slice(0, 10) === iso);
      const sessionsSet = new Set(dayVisits.map(v => v.session_id).filter(Boolean));
      days.push({
        date: iso,
        label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        views: dayVisits.length,
        sessions: sessionsSet.size,
      });
    }
    return days;
  }, [visits, period]);

  // Top referrers
  const topReferrers = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      const domain = v.referrer_domain || "Direto";
      map[domain] = (map[domain] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [visits]);

  // UTM sources
  const utmSources = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      if (v.utm_source) {
        map[v.utm_source] = (map[v.utm_source] || 0) + 1;
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [visits]);

  // UTM campaigns
  const utmCampaigns = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      if (v.utm_campaign) {
        map[v.utm_campaign] = (map[v.utm_campaign] || 0) + 1;
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [visits]);

  // Devices
  const devices = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      const d = v.device_type || "desktop";
      map[d] = (map[d] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [visits]);

  // Browsers
  const browsers = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      const b = v.browser || "Outro";
      map[b] = (map[b] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [visits]);

  // OS
  const osList = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      const o = v.os || "Outro";
      map[o] = (map[o] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [visits]);

  // Top pages
  const topPages = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => {
      map[v.page_path] = (map[v.page_path] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([path, count]) => ({ path, count }));
  }, [visits]);

  const DEVICE_ICONS: Record<string, any> = {
    desktop: Monitor,
    mobile: Smartphone,
    tablet: Tablet,
  };

  const DEVICE_LABELS: Record<string, string> = {
    desktop: "Desktop",
    mobile: "Mobile",
    tablet: "Tablet",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg">Tráfego & Aquisição</h2>
          <p className="text-xs text-muted-foreground">De onde vêm seus acessos e novos usuários</p>
        </div>
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5">
          {PERIOD_OPTIONS.map(opt => (
            <Button
              key={opt.days}
              variant={period === opt.days ? "default" : "ghost"}
              size="sm"
              className="text-xs h-7 px-3"
              onClick={() => setPeriod(opt.days)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Real-time badge */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border"
      >
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
        </span>
        <div>
          <span className="text-lg font-display font-bold">{realtimeCount}</span>
          <span className="text-xs text-muted-foreground ml-2">visitantes nos últimos 5 min</span>
        </div>
        <span className="text-[10px] text-muted-foreground ml-auto">Atualiza a cada 1 min</span>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Page Views", value: formatNumber(visits.length), icon: Eye, sub: `últimos ${period} dias` },
          { label: "Sessões Únicas", value: formatNumber(uniqueSessions), icon: Users, sub: "visitantes distintos" },
          { label: "Fontes Rastreadas", value: formatNumber(utmSources.length), icon: Link2, sub: "UTM sources" },
          { label: "Campanhas", value: formatNumber(utmCampaigns.length), icon: TrendingUp, sub: "UTM campaigns" },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="p-4 rounded-xl bg-card border border-border"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <s.icon className="h-4 w-4 text-primary" />
              </div>
            </div>
            <p className="text-xl font-bold font-display">{s.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
            <p className="text-[10px] text-muted-foreground/70">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Daily views chart */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-sm">Visitas Diárias</h3>
            <p className="text-[10px] text-muted-foreground">Page views e sessões únicas</p>
          </div>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={dailyData}>
            <defs>
              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.3} />
                <stop offset="100%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="sessionsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(200, 80%, 50%)" stopOpacity={0.2} />
                <stop offset="100%" stopColor="hsl(200, 80%, 50%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis hide />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="views" name="Views" stroke="hsl(24, 95%, 53%)" strokeWidth={2} fill="url(#viewsGrad)" />
            <Area type="monotone" dataKey="sessions" name="Sessões" stroke="hsl(200, 80%, 50%)" strokeWidth={2} fill="url(#sessionsGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Referrers + Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top referrers */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-sm">Origem dos Acessos</h3>
              <p className="text-[10px] text-muted-foreground">Principais referenciadores</p>
            </div>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </div>
          {topReferrers.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-xs">
              Sem dados de referência ainda
            </div>
          ) : (
            <div className="space-y-2.5">
              {topReferrers.map((ref, i) => {
                const maxVal = topReferrers[0]?.value || 1;
                const pct = (ref.value / maxVal) * 100;
                return (
                  <div key={ref.name} className="relative">
                    <div className="flex items-center justify-between relative z-10 px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-4">{i + 1}</span>
                        <span className="text-xs font-medium truncate max-w-[180px]">{ref.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold">{formatNumber(ref.value)}</span>
                        <span className="text-[10px] text-muted-foreground">
                          ({((ref.value / visits.length) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div
                      className="absolute inset-y-0 left-0 rounded-md bg-primary/8"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Devices */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-sm">Dispositivos</h3>
              <p className="text-[10px] text-muted-foreground">Desktop vs Mobile vs Tablet</p>
            </div>
          </div>
          {devices.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-xs">
              Sem dados
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={devices} cx="50%" cy="50%" innerRadius={42} outerRadius={65} paddingAngle={3} dataKey="value">
                    {devices.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [formatNumber(v), DEVICE_LABELS[name] || name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-2">
                {devices.map((d, idx) => {
                  const Icon = DEVICE_ICONS[d.name] || Monitor;
                  const pct = visits.length > 0 ? ((d.value / visits.length) * 100).toFixed(1) : "0";
                  return (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">{DEVICE_LABELS[d.name] || d.name}</span>
                      <span className="font-semibold">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* UTM Sources + Browsers/OS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* UTM Sources */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="mb-4">
            <h3 className="font-display font-semibold text-sm">UTM Sources</h3>
            <p className="text-[10px] text-muted-foreground">Campanhas rastreadas</p>
          </div>
          {utmSources.length === 0 ? (
            <div className="flex items-center justify-center h-[160px] text-muted-foreground text-xs">
              Nenhum UTM rastreado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={utmSources} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={80} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), "Visitas"]} />
                <Bar dataKey="value" fill="hsl(24, 95%, 53%)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Browsers */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="mb-4">
            <h3 className="font-display font-semibold text-sm">Navegadores</h3>
            <p className="text-[10px] text-muted-foreground">Distribuição por browser</p>
          </div>
          <div className="space-y-2">
            {browsers.map((b, i) => {
              const pct = visits.length > 0 ? ((b.value / visits.length) * 100).toFixed(1) : "0";
              return (
                <div key={b.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-xs flex-1 truncate">{b.name}</span>
                  <span className="text-xs font-semibold">{pct}%</span>
                  <span className="text-[10px] text-muted-foreground">({formatNumber(b.value)})</span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* OS */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="mb-4">
            <h3 className="font-display font-semibold text-sm">Sistemas Operacionais</h3>
            <p className="text-[10px] text-muted-foreground">Distribuição por OS</p>
          </div>
          <div className="space-y-2">
            {osList.map((o, i) => {
              const pct = visits.length > 0 ? ((o.value / visits.length) * 100).toFixed(1) : "0";
              return (
                <div key={o.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-xs flex-1 truncate">{o.name}</span>
                  <span className="text-xs font-semibold">{pct}%</span>
                  <span className="text-[10px] text-muted-foreground">({formatNumber(o.value)})</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Top pages */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75 }}
        className="rounded-xl border border-border bg-card overflow-hidden"
      >
        <div className="p-4 border-b border-border">
          <h3 className="font-display font-semibold text-sm">Páginas Mais Visitadas</h3>
          <p className="text-[10px] text-muted-foreground">Top 10 rotas acessadas</p>
        </div>
        {topPages.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-xs">Sem dados</div>
        ) : (
          <div className="divide-y divide-border/50">
            {topPages.map((p, i) => (
              <div key={p.path} className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/30 transition-colors">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-[10px] text-muted-foreground w-5 text-right">{i + 1}</span>
                  <span className="text-xs font-mono truncate">{p.path}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <span className="text-xs font-semibold">{formatNumber(p.count)}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {((p.count / visits.length) * 100).toFixed(1)}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminTrafficTab;
